import { WATERFALL_SLUG } from 'common/constants/allocations';
import {
  BREAKPOINT_ANALYSIS_SLUG,
  CAP_TABLE_SLUG,
  CASH_FLOW_LEDGER_SLUG,
  FUND_OWNERSHIP_SLUG,
} from 'common/constants/cap-table';
import { COMPANIES_SUMMARY_SLUG, COMPANY_DAILY_NAV_SETTINGS_TAB, TAB_COMPANY_OVERVIEW } from 'common/constants/companies';
import { DOCUMENT_GENERATION_JOBS_SLUG, DOCUMENTS_SLUG } from 'common/constants/documents';
import { FINANCIAL_SLUG } from 'common/constants/financials';
import {
  DAILY_NAV_COMPANIES_TAB,
  DAILY_NAV_SETTINGS_TAB,
  DAILY_NAV_SETUP_TAB,
  FEATURE_SETTINGS_TAB,
  FIRM_WATERFALLS_SLUG,
  INTELLIGENCE_DAILY_NAV_SLUG,
  INTELLIGENCE_FIRM_SUMMARY_SLUG,
  INTELLIGENCE_SCHEDULE_OF_INVESTMENTS_SLUG,
  INTELLIGENCE_SLUG,
  INTELLIGENCE_SUMMARIES_SLUG,
  SCALAR_AI_SETTINGS_TAB,
  SCIM_CONFIGURATION_TAB,
  SINGLE_SIGN_ON_TAB,
} from 'common/constants/firms';
import { FUND_ALIAS } from 'common/constants/general';
import { VALUATIONS_SLUG } from 'common/constants/valuations';

// BASE
export const forbiddenPath = '/not-permission';
export const homePath = '/';
export const notFoundPath = '/not-found';

// AUTH
export const signInPath = '/sign-in';
export const signUpPath = '/sign-up';
export const resetPasswordPath = '/reset-password';
export const resetPasswordTokenPrefix = '/users/reset-password';
export const resetPasswordLegacyTokenPrefix = '/users/reset_password';
export const activateAccountPrefix = '/activate-account';
export const resendActivationPath = `${activateAccountPrefix}/resend`;
export const ssoCallbackPath = '/sso/callback';
export const ssoErrorPath = '/sso/error';
export const oauthAuthorizePath = '/oauth/authorize';

export const resetPasswordTokenPath = `${resetPasswordTokenPrefix}/:uid/:token`;
export const resetPasswordLegacyTokenPath = `${resetPasswordLegacyTokenPrefix}/:uid/:token`;
export const activateAccountPath = `${activateAccountPrefix}/:uid/:token`;

export const buildResetPasswordTokenPath = (uid: string, token: string) =>
  `${resetPasswordTokenPrefix}/${uid}/${token}`;

// FIRMS

export const baseFirmUrl = (firmSlug: string) => `/firms/${firmSlug}`;

export const firmCompGroupsUrl = (firmSlug: string) => `${baseFirmUrl(firmSlug)}/comp-groups`;
export const firmDocumentsUrl = (firmSlug: string, documentsTabSlug?: string) =>
  `${baseFirmUrl(firmSlug)}/documents${documentsTabSlug ? `/${documentsTabSlug}` : ''}`;
export const firmSettingsUrl = (firmSlug: string) => `${baseFirmUrl(firmSlug)}/settings`;
export const firmFeatureSettingsUrl = (firmSlug: string) =>
  `${baseFirmUrl(firmSlug)}/${FEATURE_SETTINGS_TAB}`;
export const firmIntelligenceUrl = (firmSlug: string) => `${baseFirmUrl(firmSlug)}/${INTELLIGENCE_SLUG}`;
export const firmValuationsUrl = (firmSlug: string) => `${baseFirmUrl(firmSlug)}/valuations`;

export const firmSummaryUrl = (firmSlug: string, date?: string, fundId?: string) => {
  const baseUrl = `${firmIntelligenceUrl(firmSlug)}/${INTELLIGENCE_SUMMARIES_SLUG}/${INTELLIGENCE_FIRM_SUMMARY_SLUG}`;
  const queryParams = new URLSearchParams();

  if (date) {
    queryParams.append('date', date);
  }

  if (fundId) {
    queryParams.append(FUND_ALIAS, fundId);
  }

  const queryString = queryParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

export const firmSummaryViewUrl = (firmSlug: string, viewSlug: string) =>
  `${firmIntelligenceUrl(firmSlug)}/${INTELLIGENCE_SUMMARIES_SLUG}/${viewSlug}`;

export const firmScheduleOfInvestmentsUrl = (firmSlug: string) =>
  `${firmIntelligenceUrl(firmSlug)}/${INTELLIGENCE_SCHEDULE_OF_INVESTMENTS_SLUG}`;
export const firmDailyNavUrl = (firmSlug: string) => `${firmIntelligenceUrl(firmSlug)}/${INTELLIGENCE_DAILY_NAV_SLUG}`;
export const firmHomeUrl = (firmSlug: string) => `${baseFirmUrl(firmSlug)}/home`;
export const firmAuditLogsUrl = (firmSlug: string) => `${baseFirmUrl(firmSlug)}/audit-logs`;
export const firmUserManagementUrl = (firmSlug: string) => `${baseFirmUrl(firmSlug)}/user-management`;
export const firmWaterfallUrl = (firmSlug: string) => `${baseFirmUrl(firmSlug)}/${FIRM_WATERFALLS_SLUG}`;
export const firmSingleSignOnUrl = (firmSlug: string) => `${baseFirmUrl(firmSlug)}/${SINGLE_SIGN_ON_TAB}`;
export const firmScimConfigurationUrl = (firmSlug: string) => `${baseFirmUrl(firmSlug)}/${SCIM_CONFIGURATION_TAB}`;
export const firmDailyNavSettingsUrl = (firmSlug: string) => `${baseFirmUrl(firmSlug)}/${DAILY_NAV_SETTINGS_TAB}`;
export const firmDailyNavCompaniesUrl = (firmSlug: string) => `${baseFirmUrl(firmSlug)}/${DAILY_NAV_COMPANIES_TAB}`;
export const firmDailyNavSetupUrl = (firmSlug: string) => `${baseFirmUrl(firmSlug)}/${DAILY_NAV_SETUP_TAB}`;
export const firmScalarAiSettingsUrl = (firmSlug: string) => `${baseFirmUrl(firmSlug)}/${SCALAR_AI_SETTINGS_TAB}`;

// COMPANIES

export const baseCompanyUrl = (firmSlug: string, companySlug: string) =>
  `${baseFirmUrl(firmSlug)}/companies/${companySlug}`;

export const fundOwnershipUrl = (firmSlug: string, companySlug: string) =>
  `${baseCompanyUrl(firmSlug, companySlug)}/${FUND_OWNERSHIP_SLUG}`;
export const breakpointAnalysisUrl = (firmSlug: string, companySlug: string) =>
  `${baseCompanyUrl(firmSlug, companySlug)}/${BREAKPOINT_ANALYSIS_SLUG}`;
export const cashFlowLedgerUrl = (firmSlug: string, companySlug: string) =>
  `${baseCompanyUrl(firmSlug, companySlug)}/${CASH_FLOW_LEDGER_SLUG}`;
export const captableUrl = (firmSlug: string, companySlug: string) =>
  `${baseCompanyUrl(firmSlug, companySlug)}/${CAP_TABLE_SLUG}`;
export const companySummaryUrl = (firmSlug: string, companySlug: string) =>
  `${baseCompanyUrl(firmSlug, companySlug)}/${COMPANIES_SUMMARY_SLUG}`;
// The company's Daily NAV settings, which are a TAB on the company summary page rather
// than a page of their own -- hence a query param on companySummaryUrl.
export const companyDailyNavSettingsUrl = (firmSlug: string, companySlug: string) =>
  `${companySummaryUrl(firmSlug, companySlug)}?tab=${COMPANY_DAILY_NAV_SETTINGS_TAB}`;
export const companyOverviewTabUrl = (firmSlug: string, companySlug: string) =>
  `${companySummaryUrl(firmSlug, companySlug)}?tab=${TAB_COMPANY_OVERVIEW}`;
export const documentsUrl = (firmSlug: string, companySlug: string) =>
  `${baseCompanyUrl(firmSlug, companySlug)}/${DOCUMENTS_SLUG}`;
export const documentGenerationJobsUrl = (firmSlug: string, companySlug: string) =>
  `${baseCompanyUrl(firmSlug, companySlug)}/${DOCUMENT_GENERATION_JOBS_SLUG}`;
export const financialUrl = (firmSlug: string, companySlug: string) =>
  `${baseCompanyUrl(firmSlug, companySlug)}/${FINANCIAL_SLUG}`;
export const valuationUrl = (firmSlug: string, companySlug: string) =>
  `${baseCompanyUrl(firmSlug, companySlug)}/${VALUATIONS_SLUG}`;
export const waterfallUrl = (firmSlug: string, companySlug: string) =>
  `${baseCompanyUrl(firmSlug, companySlug)}/${WATERFALL_SLUG}`;

// USERS
export const baseUserUrl = (firmSlug: string, userId?: number | null) => {
  if (userId) {
    return `${baseFirmUrl(firmSlug)}/users/${userId}`;
  }

  return `${baseFirmUrl(firmSlug)}/users`;
};

export const userProfileTabUrl = (firmSlug: string, tab: string, userId?: number | null) =>
  `${baseUserUrl(firmSlug, userId)}/${tab}`;

export const user2faUrl = (firmSlug: string) => `${baseUserUrl(firmSlug)}/two-factor-authentication`;
export const userProfileUrl = (firmSlug: string) => `${baseUserUrl(firmSlug)}/account-settings`;
export const userAuditLogsUrl = (firmSlug: string) => `${baseUserUrl(firmSlug)}/audit-logs`;
export const userSelectedPermissionsUrl = (firmSlug: string, userId: number) =>
  `${baseUserUrl(firmSlug, userId)}/permissions`;
export const userSelectedProfileUrl = (firmSlug: string, userId: number) =>
  `${baseUserUrl(firmSlug, userId)}/account-settings`;

// VALUATIONS
export const valuationPathByDateAndVersion = (
  firmSlug: string,
  companySlug: string,
  dateSlug: string,
  versionSlug: string
) => `${valuationUrl(firmSlug, companySlug)}?date=${dateSlug}&version=${versionSlug}`;
